import * as React from "react";
import { Button } from "@ic-anywhere/ic-components";
import { EventDateDisplay } from "src/v2/Components/EventDateDisplay";
import EventContactSection from "./EventContactSection";
import SpeakerContactSection from "./SpeakerContactSection";
import SidebarSection from "./SidebarSection";
import { EventContact, Event } from "src/api/events/event.types";
import { GoogleStaticMap } from "@ic-anywhere/ic-components";
import { getAddressFormatted } from "src/utils/address/addressUtil";
import { Address, InvitationStatus } from "common/event/event.types";
import { useNavigate } from "react-router";
import { buildUrlV2, ROUTES } from "src/v2/routes";
import { SGBSColor } from "@ic-anywhere/ic-components";
import { InvitationRequestControl } from "./InvitationRequests/InvitationRequestControl";

interface EventSidebarProps {
  event: Event;
  eventInvitationStatus?: InvitationStatus;
}

const getColorEvent = (invitationEventStatus?: InvitationStatus): SGBSColor => {
  if (!invitationEventStatus) {
    return "secondary";
  }

  switch (invitationEventStatus) {
    case InvitationStatus.Accepted:
      return "success";
    case InvitationStatus.DeclinedNotAvailable:
    case InvitationStatus.DeclinedNotInterested:
      return "danger";
    default:
      return "primary";
  }
};
const EventSidebar: React.FC<EventSidebarProps> = ({ event, eventInvitationStatus }) => {
  const navigate = useNavigate();
  const {
    id,
    startDate,
    endDate,
    eventManagerContacts,
    businessContacts,
    externalSpeakerContacts,
    internalSpeakerContacts,
    venue,
  } = event;
  const speakerContacts: EventContact[] = (externalSpeakerContacts ?? []).concat(internalSpeakerContacts ?? []);
  const address: Address | undefined = venue?.address;
  const colorEventStatus = getColorEvent(eventInvitationStatus);
  let messageInvitation;
  if (eventInvitationStatus === InvitationStatus.Accepted) {
    messageInvitation =
      "Your participation to this event is confirmed. You can modify your registration at your convenience.";
  } else if (
    eventInvitationStatus === InvitationStatus.DeclinedNotAvailable ||
    eventInvitationStatus === InvitationStatus.DeclinedNotInterested
  ) {
    messageInvitation =
      "You have declined your participation to this event. You can modify your registration at your convenience.";
  }

  const handleOnNavigateToRegistration = (): void => {
    navigate(buildUrlV2(ROUTES.EVENT_SLOT_SELECTION, { id }));
  };
  return (
    <div className="d-flex flex-column  col-4 bg-lvl2">
      <div className={`d-flex flex-column p-4 border-bottom text-${colorEventStatus}`}>
        <EventDateDisplay startDate={startDate} endDate={endDate} invitationStatus={eventInvitationStatus} />
        {eventInvitationStatus === undefined && <InvitationRequestControl eventId={event.id} />}
        {eventInvitationStatus === InvitationStatus.Pending && (
          <>
            <Button
              text="Register"
              color={colorEventStatus}
              className="w-100 mt-2 py-12px border"
              onClick={handleOnNavigateToRegistration}
            />
            <Button text="Decline" color="primary" btnType="flat" className="w-100 mt-2 py-12px border" />
          </>
        )}
        {[
          InvitationStatus.Accepted,
          InvitationStatus.DeclinedNotAvailable,
          InvitationStatus.DeclinedNotInterested,
        ].some((x) => x === eventInvitationStatus) && (
          <>
            <span className="mt-2">{messageInvitation}</span>
            <Button
              text="Modify registration"
              color={colorEventStatus}
              btnType="discreet"
              className="w-100 mt-2 py-12px"
              onClick={handleOnNavigateToRegistration}
            />
          </>
        )}
      </div>
      <SidebarSection icon="person" label="Organizer">
        <EventContactSection title="Event Manager" contacts={eventManagerContacts} />
        {businessContacts && businessContacts.length > 0 && (
          <EventContactSection title="Business Contact" contacts={businessContacts} />
        )}
      </SidebarSection>
      {speakerContacts && speakerContacts.length > 0 && (
        <SidebarSection icon="campaign" label="Speaker">
          <SpeakerContactSection contacts={speakerContacts} />
        </SidebarSection>
      )}
      <SidebarSection icon="place" label="Location">
        {address && getAddressFormatted(address) ? (
          <div className="d-flex flex-column gap-3">
            <span className="fs-14 text-secondary">{getAddressFormatted(address)}</span>
            <div>
              <GoogleStaticMap address={address} displayAddressLabel={false} />
            </div>
          </div>
        ) : (
          <span className="fs-14 fw-medium">Teams</span>
        )}
      </SidebarSection>
    </div>
  );
};

export default EventSidebar;
